/**
 * What the consent page does with Supabase Auth's answer to
 * `GET /oauth/authorizations/{authorization_id}`.
 *
 * The server answers in one of two shapes (supabase/auth
 * internal/api/oauthserver/authorize.go):
 * - the request's details (`authorization_id`, `client`, `scope`, ...) when
 *   the user still has to approve or deny;
 * - only `{ redirect_url }` when the signed-in user already consented to
 *   every requested scope for this client. The server has auto-approved it,
 *   so the page must send the browser straight back to the application.
 *   This shape has no `client`, which is why a second sign-in to an
 *   already-approved app used to crash on `client.name`.
 */

/** The request details, as the page reads them (every part may be absent). */
export type ConsentDetails = {
  authorization_id: string;
  /** The client's registered redirect URI (shown, never followed here). */
  redirect_uri?: string;
  client?: { id?: string; name?: string; uri?: string; logo_uri?: string };
  user?: { id?: string; email?: string };
  /** Space-separated list of requested scopes. */
  scope?: string;
};

export type ConsentStep =
  | { kind: "redirect"; url: string }
  | { kind: "consent"; details: ConsentDetails }
  | { kind: "invalid"; reason: string };

/**
 * An absolute http(s) URL, or null. Redirects come from Supabase Auth for a
 * registered client, but the page still refuses `javascript:`, `data:` and
 * relative targets before navigating anywhere.
 */
export function safeRedirectUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > 4096)
    return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  return url.protocol === "https:" || url.protocol === "http:"
    ? url.href
    : null;
}

export function consentStep(data: unknown): ConsentStep {
  if (!data || typeof data !== "object")
    return { kind: "invalid", reason: "No authorization request found" };
  const answer = data as Record<string, unknown>;
  if ("redirect_url" in answer && answer.redirect_url !== undefined) {
    const url = safeRedirectUrl(answer.redirect_url);
    return url
      ? { kind: "redirect", url }
      : {
          kind: "invalid",
          reason: "The authorization redirect is not a web address",
        };
  }
  if (typeof answer.authorization_id !== "string" || !answer.authorization_id)
    return { kind: "invalid", reason: "No authorization request found" };
  return { kind: "consent", details: answer as unknown as ConsentDetails };
}

/** Leaving the CRM for the application (a seam so tests can observe it). */
export const oauthNavigation = {
  /** Auto-approved: no history entry for a page the user never needs again. */
  replace: (url: string) => window.location.replace(url),
  assign: (url: string) => {
    window.location.href = url;
  },
};
